import {
  Menu,
  MenuItem,
  Popover,
  Position,
  Dialog,
  Button,
  Classes,
  Intent,
} from "@blueprintjs/core"
import {
  ArrowLeft,
  CaretDown,
  Info,
  ShoppingCart,
  User,
  CalendarBlank,
  CreditCard,
  ArchiveBox,
} from "@phosphor-icons/react"
import CustomCard from "components/Atoms/CustomCard"
import CustomTag from "components/Atoms/CustomTag"
import OverFlowMenu from "components/Organisms/OverFlowMenu"
import { AppPermissions } from "constants/permissions"
import useAuthorizationStrict from "hooks/useAuthorizationStrict"
import dayjs from "dayjs"
import {
  useAddUpdateUserCreditMutation,
  useCreditDetailQuery,
} from "generated/graphql"
import React, { useMemo, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import ReactTable from "react-table"

const CREDIT_STATUS_MAP: Record<
  string,
  {
    label: string
    intent: "success" | "warning" | "danger" | "none" | "primary"
  }
> = {
  ACT: { label: "Active", intent: "success" },
  APR: { label: "Approved", intent: "primary" },
  EXP: { label: "Expired", intent: "none" },
  REJ: { label: "Rejected", intent: "danger" },
  REQ: { label: "Requested", intent: "warning" },
  SUS: { label: "Suspended", intent: "danger" },
}

const ORDER_STATUS_MAP: Record<
  string,
  {
    label: string
    intent: "success" | "warning" | "danger" | "none" | "primary"
  }
> = {
  CBU: { label: "Cancelled by User", intent: "danger" },
  CBV: { label: "Cancelled by Vendor", intent: "danger" },
  CMP: { label: "Completed", intent: "success" },
  CNF: { label: "Confirmed", intent: "primary" },
  DES: { label: "Despatched", intent: "primary" },
  FRJ: { label: "Full Reject", intent: "danger" },
  IPR: { label: "In Process", intent: "warning" },
  NPL: { label: "Not Placed", intent: "none" },
  PLC: { label: "Placed", intent: "primary" },
  PRJ: { label: "Partial Reject", intent: "warning" },
  RFD: { label: "Ready for Despatch", intent: "primary" },
  RFS: { label: "Ready for Schedule", intent: "primary" },
  SFD: { label: "Scheduled for Despatch", intent: "primary" },
}

const PAYMENT_STATUS_MAP: Record<
  string,
  { label: string; dotColor: string; textColor: string }
> = {
  FAL: { label: "Failed", dotColor: "bg-red-500", textColor: "text-red-600" },
  INP: {
    label: "In Process",
    dotColor: "bg-blue-500",
    textColor: "text-blue-600",
  },
  PLT: {
    label: "Pay Later",
    dotColor: "bg-blue-400",
    textColor: "text-blue-500",
  },
  PND: {
    label: "Pending",
    dotColor: "bg-yellow-500",
    textColor: "text-yellow-600",
  },
  PRT: {
    label: "Partial Payment",
    dotColor: "bg-orange-400",
    textColor: "text-orange-500",
  },
  RUF: {
    label: "Refunded",
    dotColor: "bg-gray-400",
    textColor: "text-gray-500",
  },
  SUC: {
    label: "Successful",
    dotColor: "bg-green-500",
    textColor: "text-green-600",
  },
}

const CreditDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const creditId = id || searchParams.get("id")

  const { data, loading, refetch } = useCreditDetailQuery({
    variables: {
      id: creditId,
    },
    fetchPolicy: "network-only",
    skip: !creditId,
  })

  const canUpdateCreditStatus = useAuthorizationStrict([
    AppPermissions.canUpdateCreditStatus,
  ])

  const [statusToUpdate, setStatusToUpdate] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<"unsettled" | "settled">(
    "unsettled"
  )

  const [updateCredit, { loading: updating }] = useAddUpdateUserCreditMutation({
    onCompleted: () => {
      refetch()
      setStatusToUpdate(null)
    },
  })

  const credit = data?.creditDetail

  const ORDERS_COLUMNS = useMemo(
    () => [
      {
        Header: () => (
          <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
            Tracking Code
          </div>
        ),
        accessor: "trackingCode",
        className: "text-center",
      },
      {
        Header: () => (
          <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
            Date
          </div>
        ),
        accessor: "date",
        Cell: ({ value }) => (
          <div className="flex justify-center w-full text-xs font-semibold text-gray-700">
            {value ? dayjs(value).format("DD MMM YY") : "--"}
          </div>
        ),
      },
      {
        Header: () => (
          <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
            Order Status
          </div>
        ),
        accessor: "orderStatus",
        Cell: ({ value }) => {
          const status = ORDER_STATUS_MAP[value] || {
            label: value || "--",
            intent: "none",
          }
          return (
            <div className="flex justify-center w-full">
              <CustomTag
                minimal
                intent={status.intent}
                className="w-min-24 flex justify-center text-center font-semibold text-xs"
              >
                {status.label}
              </CustomTag>
            </div>
          )
        },
      },
      {
        Header: () => (
          <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
            Payment Status
          </div>
        ),
        accessor: "paymentStatus",
        Cell: ({ value }) => {
          const payment = PAYMENT_STATUS_MAP[value] || {
            label: value || "--",
            dotColor: "bg-gray-400",
            textColor: "text-gray-600",
          }

          return (
            <div className="flex justify-center items-center w-full gap-2 text-xs">
              <span
                className={`w-1.5 h-1.5 rounded-full ${payment.dotColor}`}
              ></span>
              <span className={`font-bold ${payment.textColor}`}>
                {payment.label}
              </span>
            </div>
          )
        },
      },
      {
        Header: () => (
          <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
            Total Amount
          </div>
        ),
        accessor: "totalAmount",
        Cell: ({ value }) => (
          <div className="flex justify-center w-full">
            <span className="text-gray-800 font-bold text-xs">
              ₹{value?.toLocaleString() ?? 0}
            </span>
          </div>
        ),
      },
      {
        Header: () => (
          <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
            Amount to Settle
          </div>
        ),
        accessor: "outstandingAmount",
        Cell: ({ value }) => (
          <div className="flex justify-center w-full">
            <span className="text-gray-800 font-bold text-xs">
              ₹{value?.toLocaleString() ?? 0}
            </span>
          </div>
        ),
      },
      {
        Header: "",
        accessor: "id",
        width: 50,
        Cell: () => {
          return <OverFlowMenu items={[]} />
        },
      },
    ],
    []
  )

  if (loading) return <div className="p-4">Loading...</div>
  if (!credit) return <div className="p-4">No credit details found.</div>

  const statusMap = CREDIT_STATUS_MAP[credit.status] || {
    label: credit.status || "Unknown",
    intent: "none",
  }
  const utilizationRate = credit.totalAmount
    ? Math.round(((credit.usedAmount || 0) / credit.totalAmount) * 100)
    : 0
  const activeOrdersCount = credit.unsettledCreditOrders?.length || 0

  return (
    <div className="flex flex-col gap-4">
      {/* Header Back Button Area */}
      <div
        className="flex items-center gap-2 p-3 bg-[#F8F9FA] rounded-md cursor-pointer w-full"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft size={16} className="text-gray-600" />
        <span className="text-sm font-semibold text-gray-700">
          User Credit Summary
        </span>
      </div>

      {/* Main Profile Info Card */}
      <div className="w-full">
        <div className="flex items-start gap-4">
          <div className="bg-[#008060] w-12 h-12 rounded-xl flex items-center justify-center shrink-0 mt-1">
            <User size={24} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col w-full">
            <div className="flex flex-col">
              <span className="text-[10px] text-gray-500 font-semibold mb-1">
                User Name
              </span>
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold text-gray-800 m-0">
                  {credit.user?.fullName || "--"}
                </h2>

                {canUpdateCreditStatus ? (
                  <Popover
                    position={Position.BOTTOM_LEFT}
                    content={
                      <Menu>
                        {Object.entries(CREDIT_STATUS_MAP).map(([key, value]) => (
                          <MenuItem
                            key={key}
                            text={value.label}
                            onClick={() => {
                              if (!credit.user?.id || credit.totalAmount == null)
                                return
                              setStatusToUpdate(key)
                            }}
                          />
                        ))}
                      </Menu>
                    }
                  >
                    <CustomTag
                      minimal
                      interactive
                      intent={statusMap.intent as any}
                      className="font-semibold px-2 py-0.5 cursor-pointer flex items-center gap-1 text-xs rounded"
                    >
                      {statusMap.label} <CaretDown size={14} />
                    </CustomTag>
                  </Popover>
                ) : (
                  <CustomTag
                    minimal
                    intent={statusMap.intent as any}
                    className="font-semibold px-2 py-0.5 flex items-center gap-1 text-xs rounded"
                  >
                    {statusMap.label}
                  </CustomTag>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 bg-[#EAF2FF] p-1.5 rounded-xl w-max mt-3">
              <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full shadow-sm">
                <div className="bg-blue-100 text-blue-600 p-1 rounded-full">
                  <User size={12} weight="bold" />
                </div>
                <div className="flex flex-col pr-2">
                  <span className="text-[8px] text-gray-400 font-bold uppercase tracking-wider">
                    Created By
                  </span>
                  <span className="text-xs font-bold text-gray-800">
                    {credit.createdBy?.fullName || "--"}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full shadow-sm">
                <div className="bg-blue-100 text-blue-600 p-1 rounded-full">
                  <User size={12} weight="bold" />
                </div>
                <div className="flex flex-col pr-2">
                  <span className="text-[8px] text-gray-400 font-bold uppercase tracking-wider">
                    Updated By
                  </span>
                  <span className="text-xs font-bold text-gray-800">
                    {credit.updatedBy?.fullName || "--"}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full shadow-sm">
                <div className="bg-blue-100 text-blue-600 p-1 rounded-full">
                  <CalendarBlank size={12} weight="bold" />
                </div>
                <div className="flex flex-col pr-2">
                  <span className="text-[8px] text-gray-400 font-bold uppercase tracking-wider">
                    Created
                  </span>
                  <span className="text-xs font-bold text-gray-800">
                    {credit.createdAt
                      ? dayjs(credit.createdAt).format("MMMM D, YYYY")
                      : "--"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <CustomCard className="flex flex-col justify-center border border-gray-100 shadow-sm rounded-lg p-4">
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
            Total Credit Limit
          </span>
          <span className="text-xl font-bold text-gray-800">
            ₹{credit.totalAmount?.toLocaleString() ?? "0"}
          </span>
        </CustomCard>

        <CustomCard className="flex flex-col justify-center border border-gray-100 shadow-sm rounded-lg p-4">
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
            Used Credit
          </span>
          <span className="text-xl font-bold text-gray-800 mb-2">
            ₹{credit.usedAmount?.toLocaleString() ?? "0"}
          </span>
          <span className="flex items-center gap-1 text-[10px] font-semibold text-blue-500">
            <ShoppingCart size={12} /> {activeOrdersCount} Orders Active
          </span>
        </CustomCard>

        <CustomCard className="flex flex-col justify-center border border-gray-100 shadow-sm rounded-lg p-4">
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
            Available Credit
          </span>
          <span className="text-xl font-bold text-[#008060] mb-2">
            ₹{credit.remainingAmount?.toLocaleString() ?? "0"}
          </span>
          <span className="flex items-center gap-1 text-[10px] font-semibold text-gray-400">
            <Info size={12} />{" "}
            {credit.totalAmount
              ? Math.round(
                  ((credit.remainingAmount || 0) / credit.totalAmount) * 100
                )
              : 0}
            % Remaining
          </span>
        </CustomCard>

        <CustomCard className="flex flex-col justify-center border border-gray-100 shadow-sm rounded-lg p-4">
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
            Payment Cycle
          </span>
          <span className="text-xl font-bold text-amber-700">
            {credit.paymentCycleDays || 0} Days
          </span>
        </CustomCard>

        <CustomCard className="flex flex-col justify-center border border-gray-100 shadow-sm rounded-lg p-4">
          <div className="flex justify-between items-end mb-2">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
              Utilization Rate
            </span>
            <span className="text-[9px] font-semibold text-gray-400">Used</span>
          </div>
          <span className="text-xl font-bold text-gray-800 mb-2">
            {utilizationRate}%
          </span>
          <div className="w-full bg-gray-100 rounded-full h-1.5 mb-2 relative">
            <div
              className="bg-indigo-600 h-1.5 rounded-full absolute left-0 top-0"
              style={{ width: `${utilizationRate}%` }}
            ></div>
          </div>
          <div className="flex justify-between w-full">
            <span className="text-[9px] text-gray-400 font-medium">
              Low risk
            </span>
            <span className="text-[9px] text-gray-400 font-medium">
              High risk
            </span>
          </div>
        </CustomCard>
      </div>

      {/* Tabs and Table Area */}
      <div className="w-full mt-2 flex flex-col border border-gray-100 shadow-sm rounded-lg overflow-hidden bg-white">
        <div className="flex items-center justify-between bg-[#F4F6FB] p-2 border-b border-gray-100">
          <div className="flex items-center gap-6 px-4">
            <div
              className={`flex items-center gap-2 font-semibold pb-2 -mb-2.5 cursor-pointer border-b-2 transition-colors ${
                activeTab === "unsettled"
                  ? "text-indigo-600 border-indigo-600"
                  : "text-gray-500 border-transparent hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("unsettled")}
            >
              <CreditCard size={16} />
              <span className="text-xs">Unsettled</span>
              <span
                className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                  activeTab === "unsettled"
                    ? "bg-indigo-100 text-indigo-600"
                    : "bg-gray-200 text-gray-500"
                }`}
              >
                {credit.unsettledCreditOrders?.length || 0}
              </span>
            </div>
            <div
              className={`flex items-center gap-2 font-semibold pb-2 -mb-2.5 cursor-pointer border-b-2 transition-colors ${
                activeTab === "settled"
                  ? "text-indigo-600 border-indigo-600"
                  : "text-gray-500 border-transparent hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("settled")}
            >
              <span className="text-xs">Settled</span>
              <span
                className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                  activeTab === "settled"
                    ? "bg-indigo-100 text-indigo-600"
                    : "bg-gray-200 text-gray-500"
                }`}
              >
                {credit.settledCreditOrders?.length || 0}
              </span>
            </div>
          </div>
        </div>

        <div className="w-full bg-white">
          {activeTab === "unsettled" ? (
            credit.unsettledCreditOrders?.length > 0 ? (
              <ReactTable
                showPagination={false}
                minRows={0}
                pageSize={credit.unsettledCreditOrders.length}
                className="text-center border-none"
                style={{ height: "auto" }}
                data={credit.unsettledCreditOrders}
                columns={ORDERS_COLUMNS}
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-16 bg-white">
                <div className="w-12 h-12 bg-[#F4F6FB] rounded-full flex items-center justify-center mb-4 text-slate-500">
                  <ArchiveBox size={24} />
                </div>
                <h3 className="text-base font-bold text-gray-800 mb-1">
                  No Unsettled Credit Orders
                </h3>
                <p className="text-xs text-gray-500 max-w-sm text-center">
                  All your credit orders have been successfully settled or you
                  don't have any unsettled orders.
                </p>
              </div>
            )
          ) : credit.settledCreditOrders?.length > 0 ? (
            <ReactTable
              showPagination={false}
              minRows={0}
              pageSize={credit.settledCreditOrders.length}
              className="text-center border-none"
              style={{ height: "auto" }}
              data={credit.settledCreditOrders}
              columns={ORDERS_COLUMNS}
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-16 bg-white">
              <div className="w-12 h-12 bg-[#F4F6FB] rounded-full flex items-center justify-center mb-4 text-slate-500">
                <ArchiveBox size={24} />
              </div>
              <h3 className="text-base font-bold text-gray-800 mb-1">
                No Settled Credit Orders
              </h3>
              <p className="text-xs text-gray-500 max-w-sm text-center">
                All your credit orders have been successfully settled. You can
                review previous transactions or wait for new orders to appear
                here.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog
        isOpen={statusToUpdate !== null}
        onClose={() => setStatusToUpdate(null)}
        title="Confirm Status Update"
        icon="info-sign"
      >
        <div className={Classes.DIALOG_BODY}>
          <p>
            Are you sure you want to update the credit status to{" "}
            <strong>
              {statusToUpdate ? CREDIT_STATUS_MAP[statusToUpdate]?.label : ""}
            </strong>
            ?
          </p>
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={() => setStatusToUpdate(null)}>Cancel</Button>
            <Button
              intent={Intent.PRIMARY}
              loading={updating}
              onClick={() => {
                if (statusToUpdate) {
                  updateCredit({
                    variables: {
                      id: creditId,
                      userId: credit.user?.id,
                      totalAmount: credit.totalAmount,
                      status: statusToUpdate,
                    },
                  })
                }
              }}
            >
              Update
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  )
}

export default CreditDetails
